import React, { useState, useEffect } from "react";
import {
  Table, TableHead, TableRow, TableCell, TableBody, Box,
  IconButton, Button, Dialog, DialogTitle, DialogContent
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Voltage } from "../../models/signalUsers/voltage.ts";
import { Signal } from "../../models/signal.ts";
import EditVoltageForm from "./EditVoltageForm.tsx";
import { getSignalLabelFromList } from "../../utils/signalUtils.ts";
import { v4 as uuidv4 } from 'uuid';
import { useInvalidRowStyle } from "../../utils/getInvalidRowStyle.ts";

interface Props {
  data: Voltage[];
  onChange: (items: Voltage[]) => void;
  signalOptions: Signal[];
}

const emptyVoltage: Voltage = {
  signal: { name: "", net: "", id: "" },
  max: 4,
  pull_up_resistor: 0,
  pull_down_resistor: 10000,
  resolution: 0,
  load: 0,
  id: "", 
  index: 0
};

const VoltageTable: React.FC<Props> = ({ data, onChange, signalOptions }) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogConentIsValid, setDialogConentIsValid] = useState(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editVoltage, setEditVoltage] = useState<Voltage>(emptyVoltage);
  const getRowStyle = useInvalidRowStyle();

  const reindex = (items: Voltage[]): Voltage[] =>
    items
      .slice()
      .sort((a, b) => a.index - b.index)
      .map((item, i) => ({ ...item, index: i }));

  useEffect(() => {
    const validIds = new Set(signalOptions.map(s => s.id));
    const cleaned = data.filter(item => validIds.has(item.signal.id));
    if (cleaned.length !== data.length) {
      onChange(cleaned);
    }
  }, [signalOptions, data, onChange]);

  const handleSave = () => {
    const updated = [...data];
    if (editIndex !== null) {
      updated[editIndex] = editVoltage;
    } else {
      updated.push(editVoltage);
    }
    onChange(updated);
    setDialogConentIsValid(false);
    setOpenDialog(false);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setDialogConentIsValid(false);
    setEditIndex(null);
    setEditVoltage(emptyVoltage);
  };

  const handleAdd = () => {
    setEditIndex(null); 
    const edit = emptyVoltage;
    edit.id = uuidv4();
    edit.index = data.length;
    setEditVoltage(edit);
    setOpenDialog(true); 
  };

  return (
    <>
      {data.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Signal</TableCell>
              <TableCell>Max. Spannung (V)</TableCell>
              <TableCell>Pull-up-Widerstand (Ω)</TableCell>
              <TableCell>Pull-down-Widerstand (Ω)</TableCell>
              <TableCell>Auflösung (µV)</TableCell>
              <TableCell>Max. Strombelastung (µA)</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              const duplicateIndices = new Set<number>();
              const seen = new Map<string, number>();
              data.forEach((item, index) => {
                const key = [item.signal.id].sort().join("|");
                if (seen.has(key)) {
                  duplicateIndices.add(index);
                  duplicateIndices.add(seen.get(key)!);
                } else {
                  seen.set(key, index);
                }
              });
              return (
                <TableRow 
                  key={item.index}
                  sx={getRowStyle(duplicateIndices.has(index))}
                  >
                  <TableCell>{getSignalLabelFromList(item.signal.id, signalOptions)}</TableCell>
                  <TableCell>{item.max}</TableCell>
                  <TableCell>{item.pull_up_resistor}</TableCell>
                  <TableCell>{item.pull_down_resistor}</TableCell>
                  <TableCell>{(item.resolution*1000*1000).toFixed(0)}</TableCell>
                  <TableCell>{(item.load*1000*1000).toFixed(0)}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="primary" onClick={() => { setEditVoltage(item); setEditIndex(index); setOpenDialog(true); }}><EditIcon /></IconButton>
                    <IconButton size="small" color="error" onClick={() => onChange(reindex(data.filter((_, i) => i !== index)))}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Box mt={2} display="flex" gap={2}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => { handleAdd(); }}
          disabled={signalOptions.length === 0}
        >
          Hinzufügen
        </Button>
      </Box>
      <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Spannungsmessung bearbeiten</DialogTitle>
        <DialogContent>
          <EditVoltageForm data={editVoltage} onChange={setEditVoltage} setIsValid={setDialogConentIsValid} signalOptions={signalOptions} />
          <Box mt={2} display="flex" gap={2}>
            <Button variant="contained" onClick={handleSave} disabled={!dialogConentIsValid}>Speichern</Button>
            <Button variant="outlined" onClick={handleClose}>Abbrechen</Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VoltageTable;
